import React, { useState, useMemo, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { fromJS } from 'immutable'
import { CircularProgress, Grid } from '@mui/material'
import MuiButton from 'gui/components/common/MuiButton'
import { selectAuth } from 'services/auth/redux/selectors'
import { showErrorToast } from 'services/toast/redux/actions'
import { selectBrooklynServerUrl } from 'services/config/redux/selectors'
import { createImage, deleteImage } from 'services/images'
import { getD6ContentType } from 'services/content/d6-content-types'
import type { Image } from '@/__generated__/gql/graphql'
import './content-images-upload-field.scss'

interface ContentImagesUploadFieldProps {
  type: string
  title: string
  contentId: number
  contentType: string
  language: string
  primaryLanguage: string
  image?: Image | null
  onImageCreated?: () => void
  onImageDeleted?: () => void
}

export default function ContentImagesUploadField (props: ContentImagesUploadFieldProps) {
  const {
    contentId,
    contentType,
    language,
    primaryLanguage,
    type,
    title,
    image,
    onImageCreated,
    onImageDeleted,
  } = props
  const dispatch = useDispatch()
  const auth = useSelector(selectAuth)
  const { url: brooklynUrl } = useSelector(selectBrooklynServerUrl)

  const [isLoading, setIsLoading] = useState(false)

  // Create content relationship object
  const contentRelationship = useMemo(
    () => ({
      d6Id: contentId,
      d6ContentType: getD6ContentType(contentType),
      language,
      isSource: language === primaryLanguage,
      type,
    }),
    [type, contentId, contentType, language, primaryLanguage],
  )

  // Handle image upload
  const handleImageUpload = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      e.preventDefault()
      const file = e.target.files?.[0]
      if (file) {
        setIsLoading(true)
        const fileReader = new FileReader()
        fileReader.onload = async (fileEvent) => {
          const dataUri = fileEvent.target?.result ?? null
          const imageData = fromJS({
            contentRelationship,
            originalFileName: file.name,
            url: dataUri,
          })

          try {
            await createImage({
              image: imageData,
              auth,
              server: brooklynUrl,
              processImage: true,
            })
            onImageCreated?.()
          } catch (error: unknown) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error'
            dispatch(showErrorToast(`Could not create image: ${errorMessage}`))
          } finally {
            setIsLoading(false)
          }
        }
        fileReader.onerror = (error: unknown) => {
          const errorMessage = error instanceof Error ? error.message : 'Unknown error'
          dispatch(showErrorToast(`Could not read file: ${errorMessage}`))
          setIsLoading(false)
        }
        fileReader.readAsDataURL(file)
      }
    },
    [auth, brooklynUrl, contentRelationship, onImageCreated, dispatch],
  )

  // Handle delete image
  const handleDelete = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault()
      // eslint-disable-next-line no-restricted-globals
      if (!confirm('This will remove this image from the database.')) {
        return
      }
      if (!image?.id) {
        return
      }
      setIsLoading(true)
      try {
        await deleteImage({
          auth,
          server: brooklynUrl,
          image: fromJS({ id: image.id }),
        })
        onImageDeleted?.()
      } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error'
        dispatch(showErrorToast(`Could not delete image: ${errorMessage}`))
      } finally {
        setIsLoading(false)
      }
    },
    [image, auth, brooklynUrl, onImageDeleted, dispatch],
  )

  return (
    <Grid item xs={12} md={6}>
      <p>
        {title} ({type})
      </p>
      {image?.url ? (
        <>
          <div className="content-images-upload-field">
            <div className="content-images-upload-field__image-container">
              <img className="content-images-upload-field__image" src={image.url} alt={title} />
            </div>
          </div>
          <MuiButton variant="contained" color="error" onClick={handleDelete} disabled={isLoading}>
            Delete Image
          </MuiButton>
        </>
      ) : (
        <div className="content-images-upload-field">
          <input
            className="content-images-upload-field__input"
            type="file"
            id={`content-images-${type}`}
            onChange={handleImageUpload}
            disabled={isLoading}
          />
          <label htmlFor={`content-images-${type}`} className="content-images-upload-field__label">
            {isLoading ? (
              <CircularProgress size={60} sx={{ color: '#3a88fc' }} />
            ) : (
              'Click To Select Or Drag Image Here'
            )}
          </label>
        </div>
      )}
    </Grid>
  )
}
